"use client";

import { useEffect, useState } from "react";
import { CheckCircle, Clock, DollarSign } from "lucide-react";
import { API_ENDPOINTS } from "../../../../constants/config";

export default function LateFeeSlabVisualizer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lateFee, setLateFee] = useState({
    isEnabled: false,
    calculationType: "per_day",
    slabs: [],
  });

  useEffect(() => {
    const fetchLateFee = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.lateFeesGet, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          credentials: "include",
        });
        if (!response.ok) throw new Error("Failed to load late fee settings");
        const result = await response.json();
        const data = result.data || result;
        const slabs = Array.isArray(data.slabs) ? [...data.slabs] : [];
        slabs.sort((a, b) => {
          const as = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bs = b.startDate ? new Date(b.startDate).getTime() : 0;
          return as - bs;
        });
        setLateFee({
          isEnabled: !!data.isEnabled,
          calculationType: data.calculationType || "per_day",
          slabs,
        });
        setError("");
      } catch (e) {
        setError(e.message || "Could not load late fee settings");
      } finally {
        setLoading(false);
      }
    };
    fetchLateFee();
  }, []);

  const formatDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
      <h3 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-orange-600" />
        Late Fee Slabs - Easy Picture Guide
      </h3>

      <p className="text-sm text-gray-700 mb-3">
        Pay before the due date: <span className="font-semibold">no extra money</span>.
        If you pay late, a little extra money is added. The colored boxes show how it adds up.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500">Loading slabs...</div>
      ) : error ? (
        <div className="text-sm text-red-600">{error}</div>
      ) : !lateFee.isEnabled ? (
        <div className="text-sm text-gray-700">
          Late fees are <span className="font-semibold">turned off</span>. Pay the fee amount only.
        </div>
      ) : lateFee.slabs.length === 0 ? (
        <div className="text-sm text-gray-700">No late fee slabs are set. Paying late may not add extra money right now.</div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-gray-600">
            Type:{" "}
            <span className="font-medium capitalize">
              {lateFee.calculationType === "per_day" ? "Per day" : "Flat"}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-orange-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Slab Type</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Amount/Rate</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Date Range</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Example Calculation</th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">Max Late Fee</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-green-50 hover:bg-green-100">
                  <td className="border border-gray-300 px-3 py-2"><span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-600" /><span className="text-xs font-medium text-green-700">On Time</span></span></td>
                  <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">-</td>
                  <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-green-700">Rs0</td>
                  <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">Before due date</td>
                  <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">No late fee applied</td>
                  <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-green-700">Rs0</td>
                </tr>

                {lateFee.slabs.map((slab, idx) => {
                  const isFlat = slab.slabType === "flat";
                  const flatAmount = Number(slab.amount || slab.flatAmount || 0);
                  const perDayAmount = Number(slab.perDayAmount || 0);
                  const startDate = slab.startDate ? formatDate(slab.startDate) : "Not set";
                  const endDate = slab.endDate ? formatDate(slab.endDate) : "No end date";
                  const dateRange = slab.startDate ? `${startDate} - ${endDate}` : "Always applicable";

                  let exampleCalc = "";
                  let maxLateFee = "";
                  if (isFlat) {
                    exampleCalc = `If paid during this period: Rs${flatAmount.toLocaleString()}`;
                    maxLateFee = `Rs${flatAmount.toLocaleString()}`;
                  } else {
                    const exampleDays = 10;
                    const exampleTotal = perDayAmount * exampleDays;
                    exampleCalc = `${exampleDays} days late = Rs${perDayAmount.toLocaleString()} x ${exampleDays} = Rs${exampleTotal.toLocaleString()}`;
                    if (slab.endDate) {
                      const daysDiff = Math.ceil((new Date(slab.endDate) - new Date(slab.startDate || new Date())) / (1000 * 60 * 60 * 24));
                      const maxDays = Math.max(daysDiff, 30);
                      maxLateFee = `Rs${(perDayAmount * maxDays).toLocaleString()} (${maxDays} days)`;
                    } else {
                      maxLateFee = "Unlimited (no end date)";
                    }
                  }

                  return (
                    <tr key={idx} className={`${isFlat ? "bg-red-50 hover:bg-red-100" : "bg-yellow-50 hover:bg-yellow-100"}`}>
                      <td className="border border-gray-300 px-3 py-2">
                        <span className="inline-flex items-center gap-1"><Clock className={`h-3 w-3 ${isFlat ? "text-red-600" : "text-yellow-600"}`} /><span className={`text-xs font-medium ${isFlat ? "text-red-700" : "text-yellow-800"}`}>Late Payment</span></span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2"><span className={`text-xs font-medium ${isFlat ? "text-red-700" : "text-yellow-800"}`}>{isFlat ? "Flat Slab" : "Per Day Slab"}</span></td>
                      <td className="border border-gray-300 px-3 py-2"><span className="text-xs font-medium text-gray-800">{isFlat ? `Rs${flatAmount.toLocaleString()}` : `Rs${perDayAmount.toLocaleString()}/day`}</span></td>
                      <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">{dateRange}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">{exampleCalc}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-gray-800">{maxLateFee}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
